<script setup lang="ts">
import EnglishLessonPage from "~/features/english/components/EnglishLessonPage.vue";

const textbookAlignedPoints = [
  "Poem me speaker ek bird ko closely observe karta hai jo pehle natural routine me busy hota hai aur baad me human presence ko threat samajh kar udd jata hai.",
  "Emily Dickinson nature ko sirf beautiful nahi balki realistic bhi dikhati hain: bird worm ko raw khata hai, phir graceful flight ke sath scene se nikal jata hai.",
  "Exam prep me summary, central idea, tone, imagery aur similes sab se important parts hote hain.",
];

const chapterCoverage = [
  "Bird observation sequence",
  "Central idea",
  "Nature as beauty and survival",
  "Speaker and bird interaction",
  "Imagery and similes",
  "Human-nature distance",
];

const quickRevisionPoints = [
  "Poem ek ordinary natural moment ko closely observe karti hai.",
  "Bird me grace aur survival instinct dono nazar aate hain.",
  "Crumb offer karne par bird cautious ho kar udd jata hai.",
  "Nature ko poet soft aur harsh dono sides ke sath dikhati hai.",
  "Similes bird ki lightness aur delicacy ko highlight karte hain.",
];

const readingQuestions = [
  {
    question: "What happens in the poem A Bird Came Down the Walk?",
    answer: "Speaker ek bird ko walk par dekh kar observe karta hai. Bird worm khata hai, dew peeta hai, beetle ko pass hone deta hai, phir speaker ki presence mehsoos karke careful ho jata hai aur finally softly fly away kar jata hai.",
  },
  {
    question: "What is the central idea of the poem?",
    answer: "Central idea yeh hai ke nature me beauty aur fear dono sath mojood hote hain. Bird ka behavior ek taraf natural grace dikhata hai aur doosri taraf survival instinct aur caution ko reveal karta hai.",
  },
  {
    question: "How does the poet present nature in this poem?",
    answer: "Poet nature ko delicate aur violent dono forms me present karti hai. Bird ka worm khana natural cruelty ko dikhata hai, jab ke us ki flight aur movement beauty aur elegance ko show karti hai.",
  },
  {
    question: "What happens when the speaker offers the bird a crumb?",
    answer: "Jab speaker crumb offer karta hai to bird us gesture ko trust ke sath accept nahi karta. Woh cautious ho kar apne feathers kholta hai aur udd kar chala jata hai.",
  },
  {
    question: "What does the poem suggest about the relationship between humans and nature?",
    answer: "Poem suggest karti hai ke humans aur nature ka relation near yet distant hota hai. Human admire kar sakta hai, lekin wild creature apni instinct ki wajah se human ko potential threat samajh sakta hai.",
  },
];

const literaryPoints = [
  {
    label: "Tone",
    detail: "Tone curious, observant aur admiring hai, lekin middle part me slight tension bhi aa jati hai jab bird frightened aur cautious nazar aata hai.",
  },
  {
    label: "Imagery",
    detail: "Poem me vivid natural imagery hai: angle worm, dew, grass, beetle, velvet head, ocean aur butterflies. Yeh images poem ko visual aur delicate banati hain.",
  },
  {
    label: "Similes and Comparison",
    detail: "Bird ki eyes frightened beads ki tarah dikhai gayi hain aur us ki flight ko oars aur butterflies se compare kiya gaya hai. In similes se grace aur lightness emphasize hoti hai.",
  },
  {
    label: "Message",
    detail: "Poem ka message yeh hai ke ordinary natural moments me bhi wonder hota hai. Agar observation careful ho to nature ka complex beauty-fear balance samajh aata hai.",
  },
];

const mcqs = [
  {
    question: "What happens when the speaker offers the bird a crumb?",
    options: [
      { label: "a", text: "The bird happily comes near and stays with the speaker.", verdict: "incorrect", reason: "Bird trust develop nahi karta; woh cautious ho jata hai." },
      { label: "b", text: "The bird ignores nature and starts singing loudly.", verdict: "incorrect", reason: "Poem ka scene natural observation par based hai, dramatic singing par nahi." },
      { label: "c", text: "The bird becomes alert and flies away.", verdict: "correct", reason: "Human presence ko bird potential danger samajh kar udd jata hai." },
      { label: "d", text: "The bird drops the crumb into the grass.", verdict: "incorrect", reason: "Crumb ko accept karne ka scene nahi; reaction caution aur flight hai." },
    ],
  },
  {
    question: "The poem mainly presents nature as:",
    options: [
      { label: "a", text: "only soft and harmless", verdict: "incorrect", reason: "Bird worm bhi khata hai, jo nature ka harsher side show karta hai." },
      { label: "b", text: "both graceful and survival-driven", verdict: "correct", reason: "Poem beauty aur instinctive caution dono ko balance ke sath dikhati hai." },
      { label: "c", text: "completely controlled by human beings", verdict: "incorrect", reason: "Bird human se distance maintain karta hai, control accept nahi karta." },
      { label: "d", text: "unnatural and artificial", verdict: "incorrect", reason: "Poem deeply natural observation par based hai." },
    ],
  },
];

const answerBuilding = [
  "Summary me sequence follow karo: bird appears, eats, drinks, reacts, then flies away.",
  "Central idea wale answer me beauty aur fear dono mention karo; sirf beauty tak answer limit na karo.",
  "Literary question me simile, imagery aur tone ke 1-2 clear examples dene se answer stronger hota hai.",
  "Human-nature relation wale answer me admiration aur distance ko ek sath likho.",
];

usePageSeo({
  title: "A Bird Came Down the Walk Notes | 11th Class English Unit 3",
  description: "11th class English Unit 3 poem notes with summary, central idea, tone, imagery, similes, literary points and explained MCQs.",
  keywords: "a bird came down the walk notes, 11th class english unit 3, class 11 poem notes, a bird came down the walk summary, emily dickinson poem notes",
  breadcrumbs: [
    { name: "Home", item: "/" },
    { name: "11th English", item: "/11th/english/" },
    { name: "Poems", item: "/11th/english/poems/" },
    { name: "A Bird Came Down the Walk", item: "/11th/english/poems/a-bird-came-down-the-walk/" },
  ],
});
</script>

<template>
  <EnglishLessonPage
    unit="Unit 3"
    title="A Bird Came Down the Walk"
    intro="Yeh page poem ke natural observation, graceful imagery aur human-nature distance ko exam prep format me explain karta hai."
    :bullet-sections="[
      { title: 'Textbook-Aligned Snapshot', items: textbookAlignedPoints },
      { title: 'Unit 3 Coverage Checklist', items: chapterCoverage, layout: 'grid' },
      { title: 'Quick Revision Points', items: quickRevisionPoints },
      { title: 'Answer-Building Notes', items: answerBuilding }
    ]"
    :text-sections="[
      { title: 'Reading and Explanation Answers', items: readingQuestions.map((item) => ({ title: item.question, description: item.answer })) },
      { title: 'Literary Points', items: literaryPoints.map((item) => ({ title: item.label, description: item.detail })) }
    ]"
    :mcq-sections="[
      { title: 'MCQs with Option Explanation', items: mcqs }
    ]"
  />
</template>





